import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { SiteShell } from "@/components/site/SiteShell";
import { buildHead } from "@/lib/seo-head";
import { Section } from "@/components/site/Blocks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import type { LendingGuideline } from "@/data/lending-guidelines.types";
import {
  syncLendingGuidelines,
  listLendingGuidelines,
} from "@/lib/lending-guidelines.functions";

export const Route = createFileRoute("/lo/guidelines")({
  ssr: false,
  head: () =>
    buildHead({
      title: "Loan Officer Lending Guidelines Library | Internal Tools",
      description: "Internal source-of-truth library of lender product guidelines for loan officer reference and AI knowledge base syncing.",
      url: "/lo/guidelines",
      noindex: true,
      localBusiness: false,
    }),
  component: GuidelinesPage,
});

function GuidelinesPage() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [query, setQuery] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [docs, setDocs] = useState<LendingGuideline[]>([]);
  const [loadError, setLoadError] = useState(false);
  const sync = useServerFn(syncLendingGuidelines);
  const loadDocs = useServerFn(listLendingGuidelines);

  useEffect(() => {
    let active = true;
    supabase.auth.getUser().then(async ({ data }) => {
      if (!active) return;
      if (!data.user) {
        navigate({ to: "/auth", search: { redirect: "/lo/guidelines" }, replace: true });
        return;
      }
      try {
        const res = await loadDocs({ data: undefined });
        if (!active) return;
        setDocs(res.documents);
      } catch {
        if (active) setLoadError(true);
      } finally {
        if (active) setChecking(false);
      }
    });
    return () => {
      active = false;
    };
  }, [navigate, loadDocs]);

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return docs;
    return docs.filter((g) => {
      const hay = [
        g.programName,
        g.docTitle,
        g.lenderLabel,
        g.summary,
        g.productSlugs.join(" "),
        g.keyTerms.map((t) => `${t.label} ${t.value}`).join(" "),
        g.sections.map((s) => `${s.heading} ${s.bullets.join(" ")}`).join(" "),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [query, docs]);

  async function handleSync() {
    setSyncing(true);
    try {
      const res = await sync({ data: undefined });
      toast.success(`Synced ${res.count} guideline documents to the knowledge base.`);
    } catch {
      toast.error("Sync failed. Confirm your staff access and try again.");
    } finally {
      setSyncing(false);
    }
  }

  if (checking) {
    return (
      <SiteShell>
        <Section>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking your access…
          </div>
        </Section>
      </SiteShell>
    );
  }

  if (loadError) {
    return (
      <SiteShell>
        <Section>
          <h1 className="text-2xl font-semibold tracking-tight">Lending guidelines library</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            This library is limited to staff accounts. Ask an administrator to grant you loan
            officer access, then reload this page.
          </p>
        </Section>
      </SiteShell>
    );
  }



  return (
    <SiteShell>
      <Section>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Lending guidelines library</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Internal source of truth transcribed from lender product matrices. The public site and
              the AI assistant must never quote terms that are not in this library.
            </p>
          </div>
          <Button onClick={handleSync} disabled={syncing}>
            {syncing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCcw className="mr-2 h-4 w-4" />
            )}
            Sync to AI knowledge base
          </Button>
        </div>

        <div className="mb-6 flex items-center gap-3">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a term — e.g. condotel, foreign national, cash-out, reserves"
            className="max-w-xl"
          />
          <span className="text-sm text-muted-foreground">{results.length} documents</span>
        </div>

        <div className="space-y-6">
          {results.map((g) => (
            <article key={g.slug} className="rounded-xl border bg-card p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold">{g.programName}</h2>
                  <p className="text-xs text-muted-foreground">
                    {g.docTitle} · {g.lenderLabel} · effective{" "}
                    {new Date(`${g.effectiveDate}T00:00:00Z`).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      timeZone: "UTC",
                    })}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1">
                  {g.productSlugs.map((s) => (
                    <Badge key={s} variant="secondary">
                      {s}
                    </Badge>
                  ))}
                </div>
              </div>

              <p className="mt-3 text-sm">{g.summary}</p>

              <dl className="mt-4 grid gap-2 sm:grid-cols-2">
                {g.keyTerms.map((t) => (
                  <div key={t.label} className="rounded-lg bg-muted/50 p-3">
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {t.label}
                    </dt>
                    <dd className="text-sm">{t.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-4 space-y-4">
                {g.sections.map((s) => (
                  <div key={s.heading}>
                    <h3 className="text-sm font-semibold">{s.heading}</h3>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                      {s.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5" /> Source: {g.sourceFile}
              </p>
            </article>
          ))}
        </div>
      </Section>
    </SiteShell>
  );
}
